import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { AiOutlineUser, AiOutlineLock } from "react-icons/ai";
import useAuthStore from "../../store/authStore";
import AccountSettings from "./AccountSettings";
import SecuritySettings from "./SecuritySettings";

const SettingsPanel = ({ isOpen, onClose }) => {
  const { user, changePassword, isLoading, error, clearError } = useAuthStore();
  const [activeTab, setActiveTab] = useState("account");
  const [statusMessage, setStatusMessage] = useState(null);
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  
  // Settings state
  const [settings, setSettings] = useState({
    account: {
      email: user?.email || "",
      notificationsEnabled: true,
      fullName: user?.fullName || "",
      phone: user?.phone || "",
    },
    security: {
      enableTwoFactor: false,
      changePassword: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    }
  });

  const handleChange = (section, setting, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [setting]: value
      }
    });
  };
  
  const handleSaveSettings = async () => {
    if (activeTab === "security" && settings.security.changePassword) {
      // Clear any previous messages
      setStatusMessage(null);
      clearError();
      
      // Validate password fields
      if (!settings.security.currentPassword) {
        setStatusMessage("Current password is required");
        setMessageType("error");
        return;
      }
      
      if (!settings.security.newPassword) {
        setStatusMessage("New password is required");
        setMessageType("error");
        return;
      }
      
      if (settings.security.newPassword !== settings.security.confirmPassword) {
        setStatusMessage("Passwords do not match");
        setMessageType("error");
        return;
      }
      
      // Try to change password
      const result = await changePassword(
        settings.security.currentPassword,
        settings.security.newPassword
      );
      
      if (result.success) {
        // Clear password fields on success
        handleChange("security", "currentPassword", "");
        handleChange("security", "newPassword", "");
        handleChange("security", "confirmPassword", "");
        handleChange("security", "changePassword", false);
        
        setStatusMessage(result.message);
        setMessageType("success");
      } else {
        setStatusMessage(result.message);
        setMessageType("error");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Settings Panel */}
      <div className={`fixed right-0 top-0 h-full w-96 max-w-full bg-white shadow-lg z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Settings</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <IoMdClose size={24} className="text-gray-600" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-1/3 border-r border-gray-200 bg-gray-50">
              <nav className="flex flex-col p-2">
                <button 
                  onClick={() => setActiveTab("account")}
                  className={`flex items-center p-3 rounded-md text-left mb-1 ${activeTab === "account" ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"}`}
                >
                  <AiOutlineUser className="mr-2" size={18} />
                  <span>Account</span>
                </button>
                <button 
                  onClick={() => setActiveTab("security")}
                  className={`flex items-center p-3 rounded-md text-left mb-1 ${activeTab === "security" ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"}`}
                >
                  <AiOutlineLock className="mr-2" size={18} />
                  <span>Security</span>
                </button>
              </nav>
            </div>
            
            {/* Main Content */}
            <div className="w-2/3 overflow-y-auto p-4">
              {activeTab === "account" && (
                <AccountSettings 
                  settings={settings} 
                  handleChange={handleChange} 
                />
              )}
              
              {activeTab === "security" && (
                <SecuritySettings 
                  settings={settings} 
                  handleChange={handleChange} 
                />
              )}
              
              {/* Status message */}
              {statusMessage && (
                <div className={`mt-4 p-3 rounded ${messageType === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                  {statusMessage}
                </div>
              )}
            </div>
          </div>
          
          {/* Footer with save button */}
          <div className="p-4 border-t border-gray-200">
            {/* {activeTab === "security" && settings.security.changePassword ? ( */}
              <button
                onClick={handleSaveSettings}
                disabled={isLoading}
                className={`w-full py-2 px-4 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
         
              {/* <p className="text-center text-sm text-gray-500">
                {activeTab === "security" ? 
                  "Select 'Change Password' to modify your password" : 
                  "Account settings can be updated by administrators"}
              </p> */}
            {/* )} */}
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel; 